import { computed } from 'vue'
import { useCartStore, type CartItem } from './store/cart'
import { useOrderStore, type Order, type OrderItem } from './store/order'

export type { Order, OrderItem, CartItem }

export function useCartSummary() {
  const cartStore = useCartStore()

  return {
    itemsCount: computed(() => cartStore.itemsCount),
    initCart: () => cartStore.initCart(),
  }
}

export async function bootstrapCartOnAppMount() {
  const cartStore = useCartStore()
  return cartStore.initCart()
}

/**
 * Публичный контракт add-to-cart действий для карточек товара вне order-модуля
 * (catalog/ProductCard, catalog/ProductQuickView и т.п.) - без прямого доступа
 * к store, ценам и синхронизации гостевой корзины.
 */
export function useCartActions() {
  const cartStore = useCartStore()

  return {
    isInCart: (productId: string | number) => cartStore.isInCart(productId),
    addItem: (item: CartItem) => cartStore.addItem(item),
    removeItem: (productId: string | number) => cartStore.removeItem(productId),
  }
}

/**
 * Публичный контракт cart/checkout views внутримого модуля - без импорта store.
 */
export function useCartWorkspace() {
  const cartStore = useCartStore()

  return {
    items: computed(() => cartStore.items),
    isEmpty: computed(() => cartStore.isEmpty),
    itemsCount: computed(() => cartStore.itemsCount),
    totalAmount: computed(() => cartStore.totalAmount),
    taxAmount: computed(() => cartStore.taxAmount),
    updateQuantity: (productId: string | number, quantity: number) =>
      cartStore.updateQuantity(productId, quantity),
    removeItem: (productId: string | number) => cartStore.removeItem(productId),
    restoreItems: (items: CartItem[]) => cartStore.restoreItems(items),
    clearCart: () => cartStore.clearCart(),
    initCart: () => cartStore.initCart(),
  }
}

/**
 * Команды создания заказа/черновика для CheckoutView.
 */
export function useCheckoutOrders() {
  const orderStore = useOrderStore()

  return {
    loading: computed(() => orderStore.loading),
    formatOrderItems: (items: CartItem[]) => orderStore.formatOrderItems(items),
    createOrder: (
      orderData: Parameters<ReturnType<typeof useOrderStore>['createOrder']>[0],
      idempotencyKey?: string,
    ) => orderStore.createOrder(orderData, idempotencyKey),
    createDraft: (orderData: Parameters<ReturnType<typeof useOrderStore>['createDraft']>[0]) =>
      orderStore.createDraft(orderData),
    submitDraft: (
      order_id: string,
      delivery?: Parameters<ReturnType<typeof useOrderStore>['submitDraft']>[1],
    ) => orderStore.submitDraft(order_id, delivery),
  }
}

/**
 * Позиции, которые не удалось перенести на сервер после входа, остаются в localStorage
 * только до явного выхода. Иначе они достаются следующему пользователю этого браузера
 * и при каждом его входе снова дают ошибку переноса.
 */
export function discardUnsyncedGuestCart() {
  localStorage.removeItem('cart_items')
}

/** Side-effect hooks for auth session lifecycle (без reach-in в store). */
export async function syncCartOnAuthChange(): Promise<{ warnings: string[] }> {
  const result = await useCartStore().initCart()
  return { warnings: result.warnings }
}

/**
 * Публичный контракт списка заказов покупателя для Profile-модуля.
 * Владение данными и HTTP-маппингом остаётся в order/store; наружу отдаём
 * только стабильный набор полей/методов, которыми пользуется history view.
 */
export function useOrderHistory() {
  const orderStore = useOrderStore()

  return {
    orders: computed(() => orderStore.orders),
    loading: computed(() => orderStore.loading),
    error: computed(() => orderStore.error),
    fetchOrders: (
      page?: number,
      limit?: number,
      status?: string,
      dateFrom?: string,
      dateTo?: string,
    ) => orderStore.fetchOrders(page, limit, status, dateFrom, dateTo),
    cancelOrder: (orderId: string) => orderStore.cancelOrder(orderId),
    getOrderStatusColor: (status: string) => orderStore.getOrderStatusColor(status),
    canCancelOrder: (order: Order) => orderStore.canCancelOrder(order),
  }
}

/**
 * Публичный контракт детальной страницы заказа для Profile-модуля.
 */
export function useOrderDetail() {
  const orderStore = useOrderStore()

  return {
    currentOrder: computed(() => orderStore.currentOrder),
    loading: computed(() => orderStore.loading),
    error: computed(() => orderStore.error),
    fetchOrder: (id: string) => orderStore.fetchOrder(id),
    cancelOrder: (orderId: string) => orderStore.cancelOrder(orderId),
    getOrderStatusText: (status: string) => orderStore.getOrderStatusText(status),
    getOrderStatusColor: (status: string) => orderStore.getOrderStatusColor(status),
    canCancelOrder: (order: Order) => orderStore.canCancelOrder(order),
    canRepeatOrder: (order: Order) => orderStore.canRepeatOrder(order),
  }
}
